
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const currentLang = i18n.language;

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "15 de Janeiro de 2025";

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95'}`}>
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img src="https://static.readdy.ai/image/0e4a9c2d00ce897da69d164242b1c444/2b149d9c0dcc0f774da5ab5b579eeb2d.jpeg" alt="Amiraco Tax Service" className="h-12 w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium cursor-pointer">{t('nav.home')}</Link>
            
            {/* Language Switcher */}
            <div className="flex items-center gap-2 border-l border-gray-200 pl-6">
              <button 
                onClick={() => changeLanguage('en')} 
                className={`w-8 h-8 flex items-center justify-center rounded-full overflow-hidden transition-all cursor-pointer ${currentLang === 'en' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
                title="English"
              >
                <img src="https://flagcdn.com/w40/us.png" alt="English" className="w-full h-full object-cover" />
              </button>
              <button 
                onClick={() => changeLanguage('pt')} 
                className={`w-8 h-8 flex items-center justify-center rounded-full overflow-hidden transition-all cursor-pointer ${currentLang === 'pt' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
                title="Português"
              >
                <img src="https://flagcdn.com/w40/br.png" alt="Português" className="w-full h-full object-cover" />
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-gray-700 w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </nav>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="container mx-auto px-6 py-4 flex flex-col space-y-4">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium cursor-pointer">{t('nav.home')}</Link>
              
              {/* Language Switcher Mobile */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                <span className="text-gray-500 text-sm mr-2">Idioma:</span>
                <button 
                  onClick={() => changeLanguage('en')} 
                  className={`w-8 h-8 flex items-center justify-center rounded-full overflow-hidden transition-all cursor-pointer ${currentLang === 'en' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
                >
                  <img src="https://flagcdn.com/w40/us.png" alt="English" className="w-full h-full object-cover" />
                </button>
                <button 
                  onClick={() => changeLanguage('pt')} 
                  className={`w-8 h-8 flex items-center justify-center rounded-full overflow-hidden transition-all cursor-pointer ${currentLang === 'pt' ? 'ring-2 ring-blue-500 ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
                >
                  <img src="https://flagcdn.com/w40/br.png" alt="Português" className="w-full h-full object-cover" />
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
            <i className="ri-shield-check-line text-3xl text-blue-600"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {currentLang === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
          </h1>
          <p className="text-lg text-gray-600">
            {currentLang === 'pt' ? 'Última atualização:' : 'Last updated:'} {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            {/* Introdução */}
            <div className="bg-blue-50 rounded-2xl p-8 mb-10">
              <p className="text-gray-700 leading-relaxed m-0">
                {currentLang === 'pt' 
                  ? 'A Amiraco Tax Service respeita a sua privacidade e protege as informações pessoais e fiscais de seus clientes, em conformidade com as leis aplicáveis dos Estados Unidos e do estado da Califórnia, incluindo a California Consumer Privacy Act (CCPA).'
                  : 'Amiraco Tax Service respects your privacy and protects the personal and tax information of its clients, in compliance with applicable laws of the United States and the state of California, including the California Consumer Privacy Act (CCPA).'}
              </p>
            </div>

            {/* Seção 1 - Coleta de Informações */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-file-list-3-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '1. Coleta de Informações' : '1. Information Collection'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed">
                  {currentLang === 'pt' 
                    ? 'Coletamos apenas as informações necessárias para a prestação de serviços contábeis, fiscais e de consultoria, fornecidas voluntariamente pelo cliente, como dados de contato e informações fiscais.'
                    : 'We collect only the information necessary to provide accounting, tax, and consulting services, voluntarily provided by the client, such as contact information and tax data.'}
                </p>
              </div>
            </div>

            {/* Seção 2 - Uso das Informações */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-settings-3-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '2. Uso das Informações' : '2. Use of Information'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed">
                  {currentLang === 'pt' 
                    ? 'As informações coletadas são utilizadas exclusivamente para a execução dos serviços contratados, cumprimento de obrigações legais e comunicação com o cliente.'
                    : 'The information collected is used exclusively for the execution of contracted services, compliance with legal obligations, and communication with the client.'}
                </p>
              </div>
            </div>

            {/* Seção 3 - Compartilhamento de Informações */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-share-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '3. Compartilhamento de Informações' : '3. Information Sharing'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed mb-4">
                  {currentLang === 'pt' 
                    ? 'A Amiraco Tax Service não vende, aluga ou compartilha informações pessoais para fins comerciais. O compartilhamento ocorre apenas quando necessário para a prestação dos serviços ou quando exigido por lei.'
                    : 'Amiraco Tax Service does not sell, rent, or share personal information for commercial purposes. Sharing occurs only when necessary for service provision or when required by law.'}
                </p>
              </div>
            </div>

            {/* Seção 4 - Direitos dos Residentes da Califórnia */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-user-star-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '4. Direitos dos Residentes da Califórnia (CCPA)' : '4. California Residents Rights (CCPA)'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed mb-4">
                  {currentLang === 'pt' 
                    ? 'Residentes da Califórnia podem solicitar acesso, correção ou exclusão de seus dados pessoais, observadas as exigências legais de retenção.'
                    : 'California residents may request access, correction, or deletion of their personal data, subject to legal retention requirements.'}
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-eye-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700"><strong>{currentLang === 'pt' ? 'Acesso:' : 'Access:'}</strong> {currentLang === 'pt' ? 'Solicitar uma cópia das informações que temos sobre você' : 'Request a copy of the information we have about you'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-edit-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700"><strong>{currentLang === 'pt' ? 'Correção:' : 'Correction:'}</strong> {currentLang === 'pt' ? 'Solicitar a correção de informações imprecisas' : 'Request correction of inaccurate information'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-delete-bin-line text-blue-500 text-xl mt-0.5"></i>
                    <span className="text-gray-700"><strong>{currentLang === 'pt' ? 'Exclusão:' : 'Deletion:'}</strong> {currentLang === 'pt' ? 'Solicitar a exclusão de suas informações (sujeito a obrigações legais de retenção)' : 'Request deletion of your information (subject to legal retention obligations)'}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Seção 5 - Segurança das Informações */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-lock-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '5. Segurança das Informações' : '5. Information Security'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed">
                  {currentLang === 'pt' 
                    ? 'Adotamos medidas razoáveis de segurança para proteger as informações contra acesso não autorizado, uso indevido ou divulgação indevida.'
                    : 'We adopt reasonable security measures to protect information against unauthorized access, misuse, or improper disclosure.'}
                </p>
              </div>
            </div>

            {/* Seção 6 - Menores de Idade */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-shield-user-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '6. Menores de Idade' : '6. Minors'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed">
                  {currentLang === 'pt' 
                    ? 'Nossos serviços não são direcionados a menores de 13 anos.'
                    : 'Our services are not directed to individuals under 13 years of age.'}
                </p>
              </div>
            </div>

            {/* Seção 7 - Alterações na Política */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-edit-2-line text-xl text-blue-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {currentLang === 'pt' ? '7. Alterações nesta Política' : '7. Changes to This Policy'}
                </h2>
              </div>
              <div className="pl-13">
                <p className="text-gray-700 leading-relaxed">
                  {currentLang === 'pt' 
                    ? 'Esta Política de Privacidade pode ser atualizada a qualquer momento e estará sempre disponível neste site.'
                    : 'This Privacy Policy may be updated at any time and will always be available on this website.'}
                </p>
              </div>
            </div>

            {/* Seção 8 - Contato */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-mail-send-line text-xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold m-0">
                  {currentLang === 'pt' ? '8. Entre em Contato' : '8. Contact Us'}
                </h2>
              </div>
              <p className="text-white/90 leading-relaxed mb-6">
                {currentLang === 'pt' 
                  ? 'Se você tiver dúvidas sobre esta Política de Privacidade ou sobre como tratamos suas informações pessoais, entre em contato conosco:'
                  : 'If you have questions about this Privacy Policy or how we handle your personal information, please contact us:'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-building-line text-2xl"></i>
                    <div>
                      <p className="font-semibold">Amiraco Tax Service</p>
                      <p className="text-white/80 text-sm">2644 Appian Way, Suite 205<br />Pinole, CA 94564</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-2xl"></i>
                    <div>
                      <p className="font-semibold">{currentLang === 'pt' ? 'Telefone' : 'Phone'}</p>
                      <a href="tel:+14153353243" className="text-white/80 text-sm hover:text-white cursor-pointer">+1 (415) 335-3243</a>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-2xl"></i>
                    <div>
                      <p className="font-semibold">Email</p>
                      <a href="mailto:contato@amiraco.com" className="text-white/80 text-sm hover:text-white cursor-pointer">contato@amiraco.com</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src="https://static.readdy.ai/image/0e4a9c2d00ce897da69d164242b1c444/2b149d9c0dcc0f774da5ab5b579eeb2d.jpeg" alt="Amiraco Tax Service" className="h-10 w-auto brightness-0 invert" />
              <p className="text-white/60 text-sm">
                © 2025 Amiraco Tax Service. {currentLang === 'pt' ? 'Todos os direitos reservados.' : 'All rights reserved.'}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm">
                {t('nav.home')}
              </Link>
              <Link to="/privacy-policy" className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm">
                {currentLang === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
              </Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors cursor-pointer text-sm">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
